// Videos.jsx
import React, { useRef, useState, useEffect } from 'react';
import styles from "../css/Video.module.css"
import { styled } from '@mui/material/styles';
import Button from '@mui/material/Button';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import AddAPhotoIcon from '@mui/icons-material/AddAPhoto';
import { IconButton, Snackbar, Alert, CircularProgress, Typography, Box, LinearProgress, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import DeleteIcon from '@mui/icons-material/Delete';
import VideoThumbnail from './VideoThumbnail';
import VideoCallIcon from '@mui/icons-material/VideoCall';
import SaveIcon from '@mui/icons-material/Save';
import Wait from "./Wait";

import miAxios from '../api/axios';
import { useAuth } from '../hooks/AuthProvider';
import Confirmacion from './Conformacion';
import Items from './Items';

const VisuallyHiddenInput = styled('input')({
    clip: 'rect(0 0 0 0)',
    clipPath: 'inset(50%)',
    height: 1,
    overflow: 'hidden',
    position: 'absolute',
    bottom: 0,
    left: 0,
    whiteSpace: 'nowrap',
    width: 1,
});

const ProgresoCompresion = (props) => {
    if (!props.procesando) {
        return null;
    }

    return (
        <Box
            sx={{
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                backgroundColor: 'rgba(0, 0, 0, 0.7)',
                zIndex: 9999,
                color: 'white',
            }}
        >
            <CircularProgress color="inherit" />
            <Typography variant="h6" sx={{ mt: 2 }}>
                Optimizando video...
            </Typography>
            {/* 🟢 Muestra la barra de progreso si el valor es > 0 */}
            {props.progress > 0 && (
                <Box sx={{ width: '50%', mt: 2 }}>
                    <LinearProgress variant="determinate" value={props.progress} />
                    <Typography variant="body2" sx={{ mt: 1 }}>
                        {props.progress}%
                    </Typography>
                </Box>
            )}
        </Box>
    );
}

export default function Video(props) {
    const videoRef = useRef(null);
    const [videoUrl, setVideoUrl] = useState(null);
    const [videoFile, setVideoFile] = useState(null);
    const [snapshots, setSnapshots] = useState([]);
    const fileInputRef = useRef(null);
    const [mensaje, setMensaje] = useState("");
    const [procesando, setProcesando] = useState(false);
    const [procesandoCompresion, setProcesandoCompresion] = useState(false);
    const [openConfirmacion, setOpenConfirmacion] = useState(false);
    const [progressCompresion, setProgressCompresion] = useState(0)

    const [openItem, setOpenItem] = useState(false)

    const videoWorker = useRef(null);
    const user = useAuth();
    const axios = miAxios(user.handleLogout);

    const snapshotsRef = useRef(snapshots);

    useEffect(() => {
        if (props.open) {
            handleNuevo()
        }
    }, [props.open])

    useEffect(() => {
        snapshotsRef.current = snapshots;
    }, [snapshots]);

    useEffect(() => {
        videoWorker.current = new Worker(new URL('../video-worker.js', import.meta.url), { type: 'module' });

        videoWorker.current.onmessage = (event) => {
            const { processedVideoBlob, progress } = event.data;

            if (progress !== undefined) {
                setProgressCompresion(Math.round(progress * 100))
            }

            if (processedVideoBlob) {
                setProgressCompresion(0)
                setProcesandoCompresion(false)
                // 🟢 Llama a grabar() con el video procesado
                grabar(processedVideoBlob);
            }
        };

        return () => {
            if (videoWorker.current) {
                videoWorker.current.terminate();
            }
        };
    }, []);

    const handleFileChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            setVideoFile(file);
            const tempVideo = document.createElement('video');
            tempVideo.preload = 'metadata';
            tempVideo.onloadedmetadata = () => {
                window.URL.revokeObjectURL(tempVideo.src);
                if (tempVideo.duration > 120) {
                    setMensaje("El video no puede durar más de 2 minutos.");
                    event.target.value = null;
                    return;
                }
                if (tempVideo.videoHeight <= tempVideo.videoWidth) {
                    setMensaje("El video debe estar en formato vertical (portrait).");
                    event.target.value = null;
                    return;
                }
                setVideoUrl(URL.createObjectURL(file));
                setSnapshots([]);
            };
            tempVideo.src = URL.createObjectURL(file);
        }
    };

    const handleBorrar = (borrar) => {
        setSnapshots(actuales => actuales.filter((_, index) => index !== borrar));
    };

    const handleNuevo = () => {
        if (videoUrl) {
            URL.revokeObjectURL(videoUrl);
        }
        setVideoUrl(null);
        setVideoFile(null);
        setSnapshots([]);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleCaptureSnapshot = () => {
        const video = videoRef.current;
        if (video) {
            const canvas = document.createElement('canvas');
            canvas.width = video.videoWidth;
            canvas.height = video.videoHeight;
            const context = canvas.getContext('2d');
            context.drawImage(video, 0, 0, video.videoWidth, video.videoHeight);
            const imageUrl = canvas.toDataURL('image/webp');
            setSnapshots(prevSnapshots => [...prevSnapshots, imageUrl]);
        }
    };

    const dataURLtoBlob = (dataurl) => {
        const arr = dataurl.split(',');
        const mime = arr[0].match(/:(.*?);/)[1];
        const bstr = atob(arr[1]);
        let n = bstr.length;
        const u8arr = new Uint8Array(n);
        while (n--) {
            u8arr[n] = bstr.charCodeAt(n);
        }
        return new Blob([u8arr], { type: mime });
    };

    const handleGrabar = () => {
        if (puedeGrabar()) {
            setOpenConfirmacion(true);
        }
    };

    const puedeGrabar = () => {
        if (!videoFile || snapshots.length === 0) {
            setMensaje("Por favor, seleccione un video y tome al menos una instantánea.");
            return false;
        }
        return true;
    };

    const grabar = async (processedVideoBlob) => {
        setProcesando(true);
        setOpenConfirmacion(false);

        const formData = new FormData();
        formData.append('videoFile', processedVideoBlob, "story.mp4");

        snapshotsRef.current.forEach((snapshotUrl, index) => {
            const imageBlob = dataURLtoBlob(snapshotUrl);
            formData.append(`snapshotFiles`, imageBlob, `snapshot_${index}.webp`);
        });

        try {
            const response = await axios.post('videos/upload', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });
            console.log("Archivos subidos exitosamente:", response.data);
            props.cerrar()
        } catch (error) {
            console.error("Error al subir los archivos:", error);
            setMensaje("Error al subir el video.");
        } finally {
            setProcesando(false);
        }
    };

    const startProcessing = () => {
        setProcesandoCompresion(true);
        setOpenConfirmacion(false);

        videoWorker.current.postMessage({
            file: videoFile,
            fileName: videoFile.name
        });
    }

    return <Dialog open={props.open} fullWidth maxWidth="md">
        <Wait procesando={procesando} />
        <ProgresoCompresion procesando={procesandoCompresion} progress={progressCompresion} />
        <Snackbar
            open={mensaje.length === 0 ? false : true}
            autoHideDuration={6000}
            anchorOrigin={{ vertical: "top", horizontal: "center" }}
            onClose={() => { setMensaje("") }}
        >
            <Alert severity="error" onClose={() => { setMensaje("") }} variant="filled">{mensaje}</Alert>
        </Snackbar>
        <Confirmacion open={openConfirmacion} texto={"¿Desea publicar este video?"} cancelar={() => { setOpenConfirmacion(false) }} exito={startProcessing} />

        <DialogTitle>
            Nuevo Video
        </DialogTitle>
        <DialogContent>
            <div className={styles.cuerpo}>
                <div className={styles.cuerpo_video}>
                    <div>
                        <Button
                            color='coopinfa'
                            component="label"
                            role={undefined}
                            variant="contained"
                            fullWidth
                            tabIndex={-1}
                            startIcon={<CloudUploadIcon />}
                            sx={{ marginBottom: "10px" }}
                        >
                            Seleccionar video...
                            <VisuallyHiddenInput
                                type="file"
                                onChange={handleFileChange}
                                accept='video/*'
                                ref={fileInputRef}
                            />
                        </Button>
                    </div>
                    <div className={styles.video_div}>
                        {videoUrl && (<video ref={videoRef} src={videoUrl} controls style={{ maxWidth: '320px', borderRadius: '4px' }} />)}
                    </div>

                </div>
                <div className={styles.cuerpo_thumbnails}>
                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                        <Button color='coopinfa' onClick={handleCaptureSnapshot}><AddAPhotoIcon fontSize='large' /></Button>
                    </div>
                    <div className={styles.cuerpo_thumbnails_lista}>
                        {snapshots.map((snapshotUrl, index) => (
                            <div className={styles.thumbnails_div} key={index}>
                                <div className={styles.close}><IconButton onClick={() => { handleBorrar(index) }}><DeleteForeverIcon /></IconButton></div>
                                <img
                                    key={index}
                                    src={snapshotUrl}
                                    alt={`Instantánea ${index + 1}`}
                                    style={{ maxWidth: '100%', border: '1px solid #ccc' }}
                                />
                            </div>
                        ))}
                    </div>
                </div>
                <div className={styles.cuerpo_muestra}>
                    <VideoThumbnail snapshots={snapshots} interval={2000} />
                </div>
            </div>
        </DialogContent>
        <DialogActions>
            <Button variant='outlined' color='coopinfa' sx={{ minWidth: "100px" }} onClick={() => { props.cerrar() }}>Cancelar</Button>
            <Button variant='contained' color='coopinfa' sx={{ minWidth: "100px" }} onClick={handleGrabar}>Grabar</Button>
        </DialogActions>
    </Dialog>



}